import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage off' },
  { value: 'fixed', label: 'Fixed amount off' },
  { value: 'free_ride', label: 'Free ride (up to cap)' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
};

export default function Referrals() {
  const [config, setConfig] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, listRes] = await Promise.all([
        api.get('/admin/referral-config'),
        api.get('/admin/referrals'),
      ]);
      setConfig(cfgRes.data?.data || null);
      setReferrals(listRes.data?.data || []);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setRefereeField = (key, value) =>
    setConfig((c) => ({ ...c, refereeCoupon: { ...c.refereeCoupon, [key]: value } }));
  const setReferrerField = (key, value) =>
    setConfig((c) => ({ ...c, referrerReward: { ...c.referrerReward, [key]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/referral-config', {
        enabled: config.enabled,
        currency: config.currency,
        refereeCoupon: {
          discountType: config.refereeCoupon.discountType,
          discountValue: Number(config.refereeCoupon.discountValue) || 0,
          maxDiscount: config.refereeCoupon.maxDiscount === '' || config.refereeCoupon.maxDiscount == null
            ? null : Number(config.refereeCoupon.maxDiscount),
          minFare: Number(config.refereeCoupon.minFare) || 0,
          validityDays: Number(config.refereeCoupon.validityDays) || 0,
        },
        referrerReward: {
          creditAmount: Number(config.referrerReward.creditAmount) || 0,
          creditExpiryDays: Number(config.referrerReward.creditExpiryDays) || 0,
        },
      });
      toast.success('Referral settings saved');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  const rc = config.refereeCoupon || {};
  const rr = config.referrerReward || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Referrals</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          New users get a welcome coupon; referrers earn credit after the new user's first ride.
        </p>
      </div>

      {/* Config */}
      <div className="card max-w-2xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Program enabled</h3>
            <p className="text-xs text-gray-500">When off, codes are ignored and no rewards are issued.</p>
          </div>
          <button
            type="button"
            onClick={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-700 mb-3">New user welcome coupon</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Type</label>
              <select className="input text-sm" value={rc.discountType || 'fixed'}
                onChange={(e) => setRefereeField('discountType', e.target.value)}>
                {DISCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {rc.discountType !== 'free_ride' && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  {rc.discountType === 'percentage' ? 'Percent (%)' : 'Amount (QAR)'}
                </label>
                <input type="number" min={0} className="input text-sm" value={rc.discountValue ?? 0}
                  onChange={(e) => setRefereeField('discountValue', e.target.value)} />
              </div>
            )}
            {(rc.discountType === 'percentage' || rc.discountType === 'free_ride') && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">Max discount (QAR)</label>
                <input type="number" min={0} className="input text-sm" placeholder="No cap"
                  value={rc.maxDiscount ?? ''}
                  onChange={(e) => setRefereeField('maxDiscount', e.target.value)} />
              </div>
            )}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Min fare (QAR)</label>
              <input type="number" min={0} className="input text-sm" value={rc.minFare ?? 0}
                onChange={(e) => setRefereeField('minFare', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Validity (days)</label>
              <input type="number" min={0} className="input text-sm" value={rc.validityDays ?? 0}
                onChange={(e) => setRefereeField('validityDays', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-700 mb-3">Referrer reward (promo credit)</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Credit amount (QAR)</label>
              <input type="number" min={0} className="input text-sm" value={rr.creditAmount ?? 0}
                onChange={(e) => setReferrerField('creditAmount', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Credit expiry (days, 0 = never)</label>
              <input type="number" min={0} className="input text-sm" value={rr.creditExpiryDays ?? 0}
                onChange={(e) => setReferrerField('creditExpiryDays', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Referrals list */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-2">Recent referrals</h3>
        <div className="card overflow-hidden p-0">
          {referrals.length === 0 ? (
            <div className="text-center py-10 text-gray-400">No referrals yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Referrer</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">New user</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Reward</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referrals.map((r) => {
                  const name = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : '—');
                  return (
                    <tr key={r._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800">{name(r.referrer)}</td>
                      <td className="px-4 py-3 text-gray-600">{name(r.referee)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.referrerReward ? `${r.referrerReward}` : '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{fmtDate(r.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
